import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Pipe({
  name: 'safeHtml'
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {
    // avoid empty constructor
  }

  /**
   * Minimal sanitizer for complaint text to prevent oversized HTML breaking table layout at pageSize=100.
   * - Removes script/style/iframe/object/embed/meta/link tags and their content.
   * - Removes <img>, <svg> and inline event handlers.
   * - Converts block tags to line breaks.
   * - Strips all remaining tags.
   * - Collapses whitespace & limits length if maxLength provided.
   * Usage in template (truncate for overview table):
   * [innerHTML]="complaint.text | safeHtml:250"
   * Without param it keeps full (still cleaned) text.
   */
  transform(input: any, maxLength?: number) {
    if (!input || typeof input !== 'string') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    let html = input;

    // Remove dangerous container tags with content
    html = html.replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '');
    // Remove remaining opening/closing of those + meta/link
    html = html.replace(/<\/?(script|style|iframe|object|embed|meta|link)[^>]*>/gi, '');
    // Remove images & svg
    html = html.replace(/<img[^>]*>/gi, '').replace(/<svg[\s\S]*?<\/svg>/gi, '');
    // Remove event handler attributes & javascript/data href/src quickly
    html = html.replace(/ on[a-zA-Z]+="[^"]*"/g, '')
               .replace(/ on[a-zA-Z]+='[^']*'/g, '')
               .replace(/(href|src)\s*=\s*"javascript:[^"]*"/gi, '')
               .replace(/(href|src)\s*=\s*'javascript:[^']*'/gi, '')
               .replace(/(href|src)\s*=\s*"data:[^"]*"/gi, '')
               .replace(/(href|src)\s*=\s*'data:[^']*'/gi, '');

    // Replace block-level tags with line breaks so text separation is preserved
    html = html.replace(/<(p|div|br|tr|li|h[1-6]|table|thead|tbody|ul|ol)[^>]*>/gi, '\n');
    html = html.replace(/<\/(p|div|tr|li|h[1-6]|table|thead|tbody|ul|ol)>/gi, '\n');

    // Strip all remaining tags
    html = html.replace(/<[^>]+>/g, '');

    // Decode a few common HTML entities (simple map)
    html = html.replace(/&nbsp;/gi, ' ')
               .replace(/&amp;/gi, '&')
               .replace(/&lt;/gi, '<')
               .replace(/&gt;/gi, '>')
               .replace(/&quot;/gi, '"')
               .replace(/&#39;/gi, "'");

    // Collapse whitespace & line breaks
    html = html.replace(/[ \t]+/g, ' ')
               .replace(/\n{3,}/g, '\n\n')
               .trim();

    // Truncate if maxLength given
    if (typeof maxLength === 'number' && maxLength > 0 && html.length > maxLength) {
      html = `${html.slice(0, maxLength).trimEnd()}'…'`;
    }

    // Convert newlines to <br> for HTML display
    const safe = html.replace(/\n/g, '<br>');
    return this.sanitizer.bypassSecurityTrustHtml(safe);
  }
}
